import { IKey } from "dto/IKey";
import { ItemKind } from "dto/IKindItems";

// Moduł RADIX — import paczek dokumentów z programów RADIX (WIP / PoGrun / GOK).
// Kontrakty 1:1 z BE: Kwantum.LegalMail.Data/Radix/RadixDtos.cs (PascalCase).

/** Program RADIX (BE: RadixProgram). */
export enum ERadixProgram {
  /** Windykacja i płatności — upomnienia. */
  WIP = 0,
  /** Podatki od nieruchomości i gruntów — decyzje podatkowe. */
  PoGrun = 1,
  /** Gospodarka odpadami komunalnymi — zawiadomienia o zmianie stawki. */
  GOK = 2,
}

/** Skąd brać adres odbiorcy przy imporcie (BE: RadixAddressType). */
export enum ERadixAddressType {
  AddressOfResidence = 0,
  RealEstateAddress = 1,
}

/** Widok listy modułu (BE: RadixListView). */
export enum ERadixListView {
  Decisions = 0,
  Reminders = 1,
  Notifications = 2,
  ToSend = 3,
  Sent = 4,
}

/** Status obiegu pisma (BE: DocumentInternalStatus — numery zgodne z Magistratem). */
export enum ERadixInternalStatus {
  Project = 0,
  GeneratedLetters = 4,
  ReadyToSign = 5,
  Sent = 7,
  ReadyToDispatch = 29,
  ReadyToDigitalSign = 30,
  SignedInternal = 31,
}

// ── Ustawienia ───────────────────────────────────────────────────────────────

export interface IRadixSettings {
  HasWip: boolean;
  HasPogrun: boolean;
  HasGok: boolean;
  /** Miejscowości lokalne (doręczenie osobiste zamiast poczty). */
  Places: string[];
  CanManage: boolean;
}

export interface ISaveRadixSettingsRequest {
  /** null = bez zmian, "" = usuń, inaczej nowy łańcuch połączenia. */
  WipConnectionString?: string | null;
  PogrunConnectionString?: string | null;
  GokConnectionString?: string | null;
  Places?: string[] | null;
}

export interface ITestRadixConnectionRequest {
  Program: ERadixProgram;
  ConnectionString?: string | null;
}

export interface ITestRadixConnectionResponse {
  Ok: boolean;
  Message?: string | null;
}

// ── Dokumenty w bazie RADIX ──────────────────────────────────────────────────

export interface IRadixSourceDocument {
  Id: number;
  Type: string;
  TypeDescription: string;
  Number?: string | null;
  RegisterNumber?: string | null;
  DecisionDate?: string | null;
  CreationDate?: string | null;
  PersonId: number;
  PersonLabel?: string | null;
  AddressLabel?: string | null;
  /** Sygnatura przesyłki, gdy dokument został już zaimportowany. */
  ParcelSignature?: string | null;
}

export interface IRadixSourceDocumentsResponse {
  Items: IRadixSourceDocument[];
}

// ── Import ───────────────────────────────────────────────────────────────────

export interface IRadixImportRequest {
  Program: ERadixProgram;
  GroupName: string;
  ParcelStoreId?: string | null;
  DeliveryKindId: number;
  AddressType: ERadixAddressType;
  IgnoreDuplicatedDecisionsForSameAddress: boolean;
  OverridePageSettings: boolean;
  LeftMargin: number;
  RightMargin: number;
  TopMargin: number;
  BottomMargin: number;
  PageHeader: number;
  PageFooter: number;
  Places: string[];
  GeneratePdf: boolean;
}

export interface IRadixImportStartResponse {
  JobId: string;
}

export interface IRadixImportProgress {
  JobId?: string | null;
  Active: boolean;
  Finished: boolean;
  Success: boolean;
  Message?: string | null;
  Current: number;
  Total: number;
  ResultMessage?: string | null;
  ImportId?: string | null;
  StartedAt?: string | null;
  FinishedAt?: string | null;
}

// ── Listy ────────────────────────────────────────────────────────────────────

export interface IRadixListRequest {
  View: ERadixListView;
  Search?: string | null;
}

export interface IRadixListDocument {
  Id: string;
  ImportId: string;
  Program: ERadixProgram;
  Title?: string | null;
  InternalNumber?: string | null;
  RadixId: number;
  RadixPersonId: number;
  DocumentNumber?: string | null;
  RegisterNumber?: string | null;
  DocumentDate?: string | null;
  ReceiverName?: string | null;
  ReceiverAddress?: string | null;
  /** Współwłaściciele bez własnej kopii (pomarańczowa kropka w Magistracie). */
  HasOtherReceivers: boolean;
  InternalStatus: ERadixInternalStatus;
  InternalStatusName: string;
  EditorDocumentId?: string | null;
  EncryptionKeyId?: string | null;
  HasPdf: boolean;
  ParcelId?: string | null;
  ParcelNumber?: string | null;
  DeliveryKindId?: number | null;
  DeliveryKindName?: string | null;
  DeliveryKindIsElectronic: boolean;
  DispatchChartId?: string | null;
  DispatchChartSignature?: string | null;
  SendDate?: string | null;
  CaseId?: string | null;
  CaseSignature?: string | null;
}

export interface IRadixListBatch {
  Id: string;
  Program: ERadixProgram;
  GroupName?: string | null;
  CreationDate: string;
  AuthorName?: string | null;
  Imported: number;
  Skipped: number;
  NotFound: number;
  MajorNumber?: number | null;
  MajorSignature?: string | null;
  HasPrintFile: boolean;
  Documents: IRadixListDocument[];
}

export interface IRadixListResponse {
  View: ERadixListView;
  IsTeamView: boolean;
  Batches: IRadixListBatch[];
}

// ── Akcje ────────────────────────────────────────────────────────────────────

export interface IRadixDocumentsActionRequest {
  ImportId?: string | null;
  DocumentIds?: string[] | null;
}

export interface IRadixDocumentsActionResponse {
  Affected: number;
  Skipped: number;
  Message?: string | null;
}

export interface IRadixGenerateForAllRequest {
  DocumentId: string;
}

export interface IRadixGenerateForAllResponse {
  Created: number;
  Documents: IRadixListDocument[];
}

/** Plik do podglądu/druku z kluczami (jak IParcelPreview biura podawczego). */
export interface IRadixFilePreview {
  Uri: string;
  EncryptionKeyId?: string | null;
  Kind: ItemKind;
  FileName: string;
  ObjectKeys: IKey[];
  Keys: IKey[];
}
